import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "./AuthContext";
import PublicHeader from "./PublicHeader";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
// This mirrors AuthContext's API_BASE behavior so drafts/resend always hit the correct backend.
const BACKEND_URL =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_BACKEND_URL &&
    String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== "undefined" &&
    String(window.location.hostname).indexOf(
      "ai-translation-frontend-sandbox"
    ) !== -1)
    ? "https://ai-translation-backend-sandbox.onrender.com"
    : "https://ai-translation-backend.onrender.com");

// Draft capture (same backend as WorkflowBuilder drafts)
const STORAGE_KEY_REG = "zab_registration_draft_v1";

async function saveRegDraft(payload) {
  try {
    const emailVal = String(payload?.lead?.email || '').trim();
    // Only persist drafts once we have an e-mail. This prevents a flood of anonymous draft rows
    // while the user is typing their name.
    if (!emailVal || emailVal.indexOf('@') === -1) {
      return;
    }
  } catch {
    return;
  }

  try {
    const res = await fetch(`${BACKEND_URL}/workflow/draft`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include", // harmless if unauthenticated
    });
    await res.json().catch(() => ({}));
  } catch {
    // non-fatal; don't block UI
  }
}

// Debounced draft save scheduler
function scheduleSave(saveTimeoutRef, payloadBuilder) {
  if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
  saveTimeoutRef.current = setTimeout(() => {
    const payload = payloadBuilder();
    // Never send password fields in a draft
    void saveRegDraft(payload);
  }, 600);
}

// --- Password strength helpers (shared rules) ---
// (Kept here for consistency / potential reuse, but no longer used by registration)
function passwordIssues(pw = "", email = "") {
  const issues = [];
  const local = (email || "").split("@")[0] || "";
  if (pw.length < 12) issues.push("At least 12 characters");
  if (!/[a-z]/.test(pw)) issues.push("At least one lowercase letter");
  if (!/[A-Z]/.test(pw)) issues.push("At least one uppercase letter");
  if (!/[0-9]/.test(pw)) issues.push("At least one number");
  if (!/[^A-Za-z0-9\s]/.test(pw)) issues.push("At least one symbol");
  if (/\s/.test(pw)) issues.push("No spaces");
  if (local && pw.toLowerCase().includes(local.toLowerCase())) {
    issues.push("Must not contain your email username");
  }
  return issues;
}
const isStrongPassword = (pw, email) => passwordIssues(pw, email).length === 0;

// Login-only authentication errors should never be displayed on the registration screen.
// These can otherwise persist from AuthContext if the user switches from Login to Sign Up
// after a failed login attempt.
const LOGIN_ONLY_AUTH_ERRORS = new Set([
  "Incorrect email or password",
  "Invalid email or password",
  "Invalid username or password",
]);

export default function RegistrationForm({ onPendingConfirmation, onGoToLogin }) {

  const { register, authError, setAuthError } = useAuth();

  React.useEffect(() => {
    // Clear any stale auth error when this screen opens. In particular, a failed
    // login message such as "Incorrect email or password" should not carry over
    // when the user switches from Login to Registration.
    setAuthError("");
  }, [setAuthError]);
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [pending, setPending] = useState(false);
  const [localError, setLocalError] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [eulaAgreed, setEulaAgreed] = useState(false);
  const [website, setWebsite] = useState("");

  const saveTimeoutRef = React.useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");
    setLocalError("");
    setResendMsg("");
    if (!captchaToken) {
      setAuthError("Please complete the CAPTCHA.");
      return;
    }
    if (!eulaAgreed) {
      setLocalError(
        "You must agree to the End User License Agreement (EULA) to register."
      );
      return;
    }

    setPending(true);
    // Registration no longer sets the password; user will set it after email verification.
    const ok = await register(
      email,
      firstName,
      lastName,
      captchaToken,
      /* eulaAgreed: */ true,
      website
    );
    setPending(false);

    // If pendingConfirmation is set in context, parent handles the UI

    // if (ok) {   // if your register() returns boolean success
    localStorage.removeItem(STORAGE_KEY_REG);
    // }
  };

  // Convert backend error to string if it's an object or array
  let displayError = authError;
  if (authError && typeof authError === "object") {
    if (Array.isArray(authError)) {
      displayError = authError.map((e, i) => (
        <div key={i}>{e.msg || JSON.stringify(e)}</div>
      ));
    } else if (authError.detail) {
      if (Array.isArray(authError.detail)) {
        displayError = authError.detail.map((e, i) => (
          <div key={i}>{e.msg || JSON.stringify(e)}</div>
        ));
      } else {
        displayError = authError.detail;
      }
    } else {
      displayError = JSON.stringify(authError);
    }
  }

  if (
    typeof displayError === "string" &&
    LOGIN_ONLY_AUTH_ERRORS.has(displayError.trim())
  ) {
    displayError = "";
  }

  // Only show "Resend code?" UI for the exact unconfirmed-user error
  const showResend =
    displayError === "E-mail address already registered. Resend code?";

  const handleResend = async () => {
    setPending(true);
    setLocalError("");
    setAuthError("");
    setResendMsg("");
    try {
      const res = worthFetch(`${BACKEND_URL}/resend-confirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const resp = await res;
      setPending(false);
      if (resp.ok) {
        setResendMsg("Confirmation e-mail sent!");
        // Switch to EmailConfirmation screen for this email!
        if (onPendingConfirmation) onPendingConfirmation(email);
      } else {
        const data = await resp.json().catch(() => ({}));
        setLocalError(
          data.detail || "Failed to resend confirmation code."
        );
      }
    } catch {
      setPending(false);
      setLocalError("Network error.");
    }
  };

  // Small wrapper to avoid accidental refactor; keeps logic unchanged.
  function worthFetch(url, opts) {
    return fetch(url, opts);
  }

  React.useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY_REG);
    if (!raw) return;

    try {
      const d = JSON.parse(raw);

      if (d.firstName) setFirstName((s) => s || d.firstName);
      if (d.lastName) setLastName((s) => s || d.lastName);
      if (d.email) setEmail((s) => s || d.email);

      if (typeof d.eulaAgreed === "boolean") {
        setEulaAgreed((e) => e || d.eulaAgreed);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY_REG);
    }
  }, []);

  React.useEffect(() => {
    const toSave = {
      firstName,
      lastName,
      email,
      eulaAgreed: !!eulaAgreed,
    };
    localStorage.setItem(STORAGE_KEY_REG, JSON.stringify(toSave));
  }, [firstName, lastName, email, eulaAgreed, website]);

  React.useEffect(() => {
    const hasAny =
      firstName.trim() || lastName.trim() || email.trim() || eulaAgreed;

    if (hasAny) {
      scheduleSave(saveTimeoutRef, () => ({
        // workflowdraft.step is an INTEGER in the DB/schema; keep this numeric to avoid 422 validation errors.
        step: 0,
        lead: {
          firstName,
          lastName,
          email,
          eulaAgreed: !!eulaAgreed,
          website,
        },
        website,
      }));
    }

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [firstName, lastName, email, eulaAgreed]);

  return (
    <>
      <PublicHeader />

      <form
        className="space-y-4 bg-white p-4 rounded max-w-md mx-auto"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <div
          className="text-center rounded-lg border px-3 py-2"
          style={{ color: "#00a7e1", borderColor: "#00a7e1" }}
        >
          <div className="font-bold">Start Your Free Trial</div>
          <div className="text-sm font-semibold leading-snug">
            <span className="block whitespace-nowrap">Create your free account and receive 25 AI credits</span>
            <span className="block whitespace-nowrap">to experience ZabFlow AI.</span>
          </div>
          <div className="text-xs mt-1">No credit card required.</div>
        </div>

        <h2 className="font-bold text-xl mb-4">Register</h2>

        <div>
          <label
            className="block font-semibold mb-1"
            htmlFor="firstName"
          >
            First Name
          </label>
          <input
            data-testid="registration-first-name"
            id="firstName"
            type="text"
            className="w-full border p-2 rounded"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={pending}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1" htmlFor="lastName">
            Last Name
          </label>
          <input
            data-testid="registration-last-name"
            id="lastName"
            type="text"
            className="w-full border p-2 rounded"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={pending}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1" htmlFor="email">
            E-mail Address
          </label>
          <input
            data-testid="registration-email"
            id="email"
            type="email"
            autoComplete="username"
            className="w-full border p-2 rounded"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={pending}
          />
        </div>

        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
        >
          <label htmlFor="registration-website">Website</label>
          <input
            id="registration-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <div data-testid="registration-recaptcha">
          {RECAPTCHA_SITE_KEY ? (
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={setCaptchaToken}
              onExpired={() => setCaptchaToken("")}
            />
          ) : (
            <div className="text-sm text-gray-500">
              reCAPTCHA is disabled in this environment.
            </div>
          )}
        </div>

        {localError && <div className="text-red-600">{localError}</div>}

        {/* Only show "Resend code?" for the unconfirmed-user message */}
        {showResend ? (
          <div className="text-red-600">
            E-mail address already registered.
            <button
              data-testid="registration-resend-confirmation"
              type="button"
              className="underline ml-1"
              style={{ color: "#00a7e1" }}
              onClick={handleResend}
              disabled={pending}
            >
              Resend code?
            </button>
            {resendMsg && (
              <div style={{ color: "#94c83d" }}>{resendMsg}</div>
            )}
          </div>
        ) : (
          displayError && (
            <div className="text-red-600">
              {displayError ===
              "User is already registered. Please login instead." ? (
                <>
                  User is already registered.&nbsp;
                  <button
                    type="button"
                    className="underline"
                    style={{ color: "#00a7e1" }}
                    onClick={() => {
                      setAuthError("");
                      if (typeof window !== "undefined")
                        window.scrollTo(0, 0);
                      if (onGoToLogin) onGoToLogin();
                    }}
                  >
                    Please login instead.
                  </button>
                </>
              ) : (
                displayError
              )}
            </div>
          )
        )}

        {/* EULA agreement */}
        <div className="flex items-start gap-3">
          <input
            data-testid="registration-eula"
            id="eula"
            type="checkbox"
            className="w-5 h-5"
            checked={eulaAgreed}
            onChange={(e) => setEulaAgreed(e.target.checked)}
            disabled={pending}
          />
          <label htmlFor="eula" className="text-base">
            I agree to the{" "}
            <a
              className="underline"
              style={{ color: "#00a7e1" }}
              href="https://app.termly.io/policy-viewer/policy.html?policyUUID=d083b4a4-7dea-4898-b51e-e34e14d5a432"
              target="_blank"
              rel="noreferrer"
            >
              End User License Agreement
            </a>
            .
          </label>
        </div>

        {/* Register button: brand blue background, white text */}
        <button
          data-testid="registration-submit"
          type="submit"
          className="text-white px-4 py-2 rounded w-full disabled:opacity-50 hover:opacity-90"
          style={{ backgroundColor: "#00a7e1" }}
          disabled={pending}
        >
          {pending ? "Registering..." : "Register"}
        </button>

        {/* "Already have an account? Login" link */}
        <div className="text-center mt-4 mb-2">
          <button
            data-testid="registration-signin"
            type="button"
            className="underline"
            style={{ color: "#00a7e1" }}
            onClick={() => {
              setLocalError("");
              setAuthError("");
              setResendMsg("");
              if (onGoToLogin) onGoToLogin();
            }}
          >
            Already have an account? Login
          </button>
        </div>
      </form>
    </>
  );
}